// ============================================================================
// GAME MAP
// ============================================================================

import { MapData, Vector2, Rectangle, Team, SpawnPoint } from '@/shared/types';
import { CollisionBody, ColliderType, CollisionLayers, CollisionWorld } from '@/engine/CollisionSystem';
import { generateId } from '@/shared/utils';

export class GameMap {
  public readonly data: MapData;
  private collisionBodies: CollisionBody[] = [];

  constructor(data: MapData) {
    this.data = data;
    this.buildCollision();
  }

  private buildCollision(): void {
    // Convert tile map to collision bodies
    for (let y = 0; y < this.data.tiles.length; y++) {
      const row = this.data.tiles[y];
      if (!row) continue;

      for (let x = 0; x < row.length; x++) {
        const tile = row[x];
        
        // 0 = empty, 1 = wall
        if (tile === 1) {
          const body: CollisionBody = {
            id: generateId('wall'),
            position: {
              x: x * this.data.tileSize,
              y: y * this.data.tileSize,
            },
            velocity: { x: 0, y: 0 },
            collider: {
              type: ColliderType.Box,
              offset: { x: 0, y: 0 },
              width: this.data.tileSize,
              height: this.data.tileSize,
            },
            layer: CollisionLayers.WALL,
            mask: CollisionLayers.ALL,
            isStatic: true,
            isTrigger: false,
          };
          this.collisionBodies.push(body);
        }
      }
    }

    // Add prop colliders
    for (const prop of this.data.props) {
      if (prop.collider) {
        const body: CollisionBody = {
          id: generateId('prop'),
          position: prop.position,
          velocity: { x: 0, y: 0 },
          collider: {
            type: ColliderType.Box,
            offset: { x: 0, y: 0 },
            width: prop.collider.width,
            height: prop.collider.height,
          },
          layer: CollisionLayers.WALL,
          mask: CollisionLayers.ALL,
          isStatic: true,
          isTrigger: false,
        };
        this.collisionBodies.push(body);
      }
    }
  }

  registerCollision(world: CollisionWorld): void {
    for (const body of this.collisionBodies) {
      world.addBody(body);
    }
  }

  getSpawnPoints(team: Team): SpawnPoint[] {
    return this.data.spawnPoints.filter(sp => sp.team === team);
  }

  getSpawnPoint(team: Team, index: number): Vector2 {
    const spawns = this.getSpawnPoints(team);
    const spawn = spawns[index % spawns.length];
    return spawn ? { ...spawn.position } : { x: 0, y: 0 };
  }

  getSpikeSites(): Rectangle[] {
    return this.data.spikeSites;
  }

  isInSpikeSite(position: Vector2): number {
    for (let i = 0; i < this.data.spikeSites.length; i++) {
      const site = this.data.spikeSites[i]!;
      if (
        position.x >= site.x &&
        position.x <= site.x + site.width &&
        position.y >= site.y &&
        position.y <= site.y + site.height
      ) {
        return i;
      }
    }
    return -1;
  }

  getTileAt(worldX: number, worldY: number): number {
    const tileX = Math.floor(worldX / this.data.tileSize);
    const tileY = Math.floor(worldY / this.data.tileSize);

    if (tileY < 0 || tileY >= this.data.tiles.length) return 1;
    const row = this.data.tiles[tileY];
    if (!row || tileX < 0 || tileX >= row.length) return 1;

    return row[tileX] ?? 1;
  }

  isWalkable(worldX: number, worldY: number): boolean {
    return this.getTileAt(worldX, worldY) === 0;
  }

  getWidth(): number {
    return this.data.width;
  }

  getHeight(): number {
    return this.data.height;
  }

  getTileSize(): number {
    return this.data.tileSize;
  }
}

// ============================================================================
// MAP REGISTRY - All available maps
// ============================================================================

export interface MapInfo {
  id: string;
  name: string;
  description: string;
  hasSpikeSites: boolean;
  recommended: string; // Recommended game mode
}

// Registry of all available maps
export const MAP_REGISTRY: MapInfo[] = [
  // Deathmatch maps (no spike sites)
  { id: 'neon_arena', name: 'Neon Arena', description: 'Classic symmetrical arena', hasSpikeSites: false, recommended: 'Deathmatch' },
  { id: 'reactor_core', name: 'Reactor Core', description: 'Circular arena with central cover', hasSpikeSites: false, recommended: 'Deathmatch' },
  { id: 'grid_maze', name: 'Grid Maze', description: 'Complex maze with many corners', hasSpikeSites: false, recommended: 'Deathmatch' },
  { id: 'neon_tower', name: 'Neon Tower', description: 'Vertical tower with open floors', hasSpikeSites: false, recommended: 'Deathmatch' },
  { id: 'crossfire', name: 'Crossfire', description: 'Four-way intersection battle', hasSpikeSites: false, recommended: 'Deathmatch' },
  { id: 'warehouse', name: 'Warehouse', description: 'Industrial storage facility', hasSpikeSites: false, recommended: 'Deathmatch' },
  // Spike maps (have spike sites)
  { id: 'cyber_district', name: 'Cyber District', description: 'Urban streets with spike sites', hasSpikeSites: true, recommended: 'Data Spike' },
  { id: 'helix_labs', name: 'Helix Labs', description: 'Tight corridors and open labs', hasSpikeSites: true, recommended: 'Data Spike' },
  { id: 'sky_bridge', name: 'Sky Bridge', description: 'Three lanes connected by bridges', hasSpikeSites: true, recommended: 'Data Spike' },
  { id: 'data_vault', name: 'Data Vault', description: 'Secure vault with multiple entries', hasSpikeSites: true, recommended: 'Data Spike' },
  { id: 'server_farm', name: 'Server Farm', description: 'High-tech data center', hasSpikeSites: true, recommended: 'Data Spike' },
  { id: 'nexus_hub', name: 'Nexus Hub', description: 'Central network control room', hasSpikeSites: true, recommended: 'Data Spike' },
];

// Get map by ID
export function getMapById(mapId: string): GameMap {
  switch (mapId) {
    case 'neon_arena':
      return createNeonArenaMap();
    case 'cyber_district':
      return createCyberDistrictMap();
    case 'helix_labs':
      return createHelixLabsMap();
    case 'reactor_core':
      return createReactorCoreMap();
    case 'grid_maze':
      return createGridMazeMap();
    case 'sky_bridge':
      return createSkyBridgeMap();
    case 'data_vault':
      return createDataVaultMap();
    case 'neon_tower':
      return createNeonTowerMap();
    case 'crossfire':
      return createCrossfireMap();
    case 'warehouse':
      return createWarehouseMap();
    case 'server_farm':
      return createServerFarmMap();
    case 'nexus_hub':
      return createNexusHubMap();
    default:
      console.warn(`Unknown map ID: ${mapId}, falling back to neon_arena`);
      return createNeonArenaMap();
  }
}

// ============================================================================
// MAP 1: NEON ARENA - Classic symmetrical deathmatch arena (no spike sites)
// ============================================================================

export function createNeonArenaMap(): GameMap {
  const tileSize = 32;
  const width = 40;
  const height = 30;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Center pillars (larger)
      else if (x >= 18 && x <= 21 && y >= 13 && y <= 16) {
        row.push(1);
      }
      // Left side cover (staggered)
      else if (
        (x >= 6 && x <= 8 && y >= 8 && y <= 10) ||
        (x >= 10 && x <= 12 && y >= 13 && y <= 16) ||
        (x >= 6 && x <= 8 && y >= 19 && y <= 21)
      ) {
        row.push(1);
      }
      // Right side cover (mirrored)
      else if (
        (x >= 31 && x <= 33 && y >= 8 && y <= 10) ||
        (x >= 27 && x <= 29 && y >= 13 && y <= 16) ||
        (x >= 31 && x <= 33 && y >= 19 && y <= 21)
      ) {
        row.push(1);
      }
      // Mid lane walls
      else if (
        (x >= 15 && x <= 17 && y === 8) ||
        (x >= 22 && x <= 24 && y === 8) ||
        (x >= 15 && x <= 17 && y === 21) ||
        (x >= 22 && x <= 24 && y === 21)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'neon_arena',
    name: 'Neon Arena',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      // Attackers (left side)
      { position: { x: 80, y: 240 }, team: Team.Attackers, index: 0 },
      { position: { x: 80, y: 340 }, team: Team.Attackers, index: 1 },
      { position: { x: 80, y: 440 }, team: Team.Attackers, index: 2 },
      { position: { x: 80, y: 540 }, team: Team.Attackers, index: 3 },
      // Defenders (right side)
      { position: { x: 1200, y: 240 }, team: Team.Defenders, index: 0 },
      { position: { x: 1200, y: 340 }, team: Team.Defenders, index: 1 },
      { position: { x: 1200, y: 440 }, team: Team.Defenders, index: 2 },
      { position: { x: 1200, y: 540 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [], // No spike sites - deathmatch map
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 2: CYBER DISTRICT - Urban streets with two spike sites
// ============================================================================

export function createCyberDistrictMap(): GameMap {
  const tileSize = 32;
  const width = 45;
  const height = 32;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Left spawn building
      else if (x >= 2 && x <= 6 && y >= 2 && y <= 6) {
        row.push(1);
      }
      else if (x >= 2 && x <= 6 && y >= height - 7 && y <= height - 3) {
        row.push(1);
      }
      // Right spawn building  
      else if (x >= width - 7 && x <= width - 3 && y >= 2 && y <= 6) {
        row.push(1);
      }
      else if (x >= width - 7 && x <= width - 3 && y >= height - 7 && y <= height - 3) {
        row.push(1);
      }
      // Site A area walls (top-right quadrant)
      else if (x >= 28 && x <= 30 && y >= 4 && y <= 8) {
        row.push(1);
      }
      else if (x >= 34 && x <= 36 && y >= 8 && y <= 10) {
        row.push(1);
      }
      // Site B area walls (bottom-right quadrant)
      else if (x >= 28 && x <= 30 && y >= height - 9 && y <= height - 5) {
        row.push(1);
      }
      else if (x >= 34 && x <= 36 && y >= height - 11 && y <= height - 9) {
        row.push(1);
      }
      // Middle corridor walls
      else if (x >= 14 && x <= 16 && y >= 12 && y <= 14) {
        row.push(1);
      }
      else if (x >= 14 && x <= 16 && y >= height - 15 && y <= height - 13) {
        row.push(1);
      }
      // Central building
      else if (x >= 20 && x <= 24 && y >= 13 && y <= 18) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'cyber_district',
    name: 'Cyber District',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      // Attackers (left side)
      { position: { x: 80, y: 300 }, team: Team.Attackers, index: 0 },
      { position: { x: 80, y: 400 }, team: Team.Attackers, index: 1 },
      { position: { x: 80, y: 500 }, team: Team.Attackers, index: 2 },
      { position: { x: 80, y: 600 }, team: Team.Attackers, index: 3 },
      // Defenders (right side, near sites)
      { position: { x: width * tileSize - 100, y: 200 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 100, y: 300 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 100, y: 700 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 100, y: 800 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 960, y: 128, width: 224, height: 160 },   // Site A (top-right)
      { x: 960, y: height * tileSize - 288, width: 224, height: 160 },  // Site B (bottom-right)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 3: HELIX LABS - Research facility with tight corridors
// ============================================================================

export function createHelixLabsMap(): GameMap {
  const tileSize = 32;
  const width = 50;
  const height = 35;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Left wing labs
      else if (
        (x >= 3 && x <= 10 && y === 8) ||
        (x >= 3 && x <= 10 && y === 16) ||
        (x === 10 && y >= 8 && y <= 16)
      ) {
        row.push(1);
      }
      // Left wing bottom labs
      else if (
        (x >= 3 && x <= 10 && y === height - 9) ||
        (x >= 3 && x <= 10 && y === height - 17) ||
        (x === 10 && y >= height - 17 && y <= height - 9)
      ) {
        row.push(1);
      }
      // Central corridor walls
      else if (
        (x >= 18 && x <= 20 && y >= 10 && y <= 12) ||
        (x >= 18 && x <= 20 && y >= height - 13 && y <= height - 11) ||
        (x >= 29 && x <= 31 && y >= 10 && y <= 12) ||
        (x >= 29 && x <= 31 && y >= height - 13 && y <= height - 11)
      ) {
        row.push(1);
      }
      // Central lab (large)
      else if (x >= 22 && x <= 27 && y >= 15 && y <= 19) {
        row.push(1);
      }
      // Site A enclosure (top right)
      else if (
        (x >= 38 && x <= 46 && y === 5) ||
        (x >= 38 && x <= 46 && y === 12) ||
        (x === 38 && y >= 5 && y <= 12)
      ) {
        row.push(1);
      }
      // Site B enclosure (bottom right)
      else if (
        (x >= 38 && x <= 46 && y === height - 6) ||
        (x >= 38 && x <= 46 && y === height - 13) ||
        (x === 38 && y >= height - 13 && y <= height - 6)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'helix_labs',
    name: 'Helix Labs',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      // Attackers (left side in labs)
      { position: { x: 64, y: 350 }, team: Team.Attackers, index: 0 },
      { position: { x: 64, y: 450 }, team: Team.Attackers, index: 1 },
      { position: { x: 64, y: 550 }, team: Team.Attackers, index: 2 },
      { position: { x: 64, y: 650 }, team: Team.Attackers, index: 3 },
      // Defenders (right side near sites)
      { position: { x: width * tileSize - 80, y: 250 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 80, y: 350 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 80, y: 750 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 80, y: 850 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 1248, y: 192, width: 256, height: 192 },   // Site A (top-right lab)
      { x: 1248, y: height * tileSize - 384, width: 256, height: 192 },  // Site B (bottom-right lab)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 4: REACTOR CORE - Circular arena with central reactor
// ============================================================================

export function createReactorCoreMap(): GameMap {
  const tileSize = 32;
  const width = 38;
  const height = 38;
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      const dx = x - centerX;
      const dy = y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Outer border (circular-ish)
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Cut corners to make it more circular
      else if (
        (x < 4 && y < 4) || (x < 4 && y > height - 5) ||
        (x > width - 5 && y < 4) || (x > width - 5 && y > height - 5)
      ) {
        row.push(1);
      }
      // Central reactor core (hollow circle)
      else if (dist >= 4 && dist <= 6) {
        // Leave gaps for entrances
        const angle = Math.atan2(dy, dx);
        const normalizedAngle = ((angle + Math.PI) / (2 * Math.PI)) * 360;
        // Gaps at 0°, 90°, 180°, 270°
        const isGap = 
          (normalizedAngle >= 355 || normalizedAngle <= 5) ||
          (normalizedAngle >= 85 && normalizedAngle <= 95) ||
          (normalizedAngle >= 175 && normalizedAngle <= 185) ||
          (normalizedAngle >= 265 && normalizedAngle <= 275);
        row.push(isGap ? 0 : 1);
      }
      // Inner ring cover
      else if (dist >= 10 && dist <= 11) {
        const angle = Math.atan2(dy, dx);
        const normalizedAngle = ((angle + Math.PI) / (2 * Math.PI)) * 360;
        // Pillars at 45°, 135°, 225°, 315°
        const isPillar = 
          (normalizedAngle >= 40 && normalizedAngle <= 50) ||
          (normalizedAngle >= 130 && normalizedAngle <= 140) ||
          (normalizedAngle >= 220 && normalizedAngle <= 230) ||
          (normalizedAngle >= 310 && normalizedAngle <= 320);
        row.push(isPillar ? 1 : 0);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'reactor_core',
    name: 'Reactor Core',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      // Attackers (left side)
      { position: { x: 100, y: centerY * tileSize - 80 }, team: Team.Attackers, index: 0 },
      { position: { x: 100, y: centerY * tileSize }, team: Team.Attackers, index: 1 },
      { position: { x: 100, y: centerY * tileSize + 80 }, team: Team.Attackers, index: 2 },
      { position: { x: 150, y: centerY * tileSize + 160 }, team: Team.Attackers, index: 3 },
      // Defenders (right side)
      { position: { x: width * tileSize - 100, y: centerY * tileSize - 80 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 100, y: centerY * tileSize }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 100, y: centerY * tileSize + 80 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 150, y: centerY * tileSize + 160 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [], // No spike sites - deathmatch arena
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 5: GRID MAZE - Complex maze with many corners and ambush points
// ============================================================================

export function createGridMazeMap(): GameMap {
  const tileSize = 32;
  const width = 42;
  const height = 32;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Grid pattern - vertical walls every 6 tiles with gaps
      else if (x % 6 === 3 && y > 2 && y < height - 3) {
        // Leave gaps at certain y positions
        if ((y >= 7 && y <= 9) || (y >= 14 && y <= 16) || (y >= 22 && y <= 24)) {
          row.push(0);
        } else {
          row.push(1);
        }
      }
      // Horizontal walls to create maze sections
      else if (y === 7 && x > 1 && x < width - 2 && x % 6 !== 0) {
        row.push(1);
      }
      else if (y === 16 && x > 1 && x < width - 2 && x % 6 !== 3) {
        row.push(1);
      }
      else if (y === 24 && x > 1 && x < width - 2 && x % 6 !== 0) {
        row.push(1);
      }
      // Corner blocks for cover
      else if (
        (x >= 8 && x <= 10 && y >= 11 && y <= 13) ||
        (x >= 20 && x <= 22 && y >= 11 && y <= 13) ||
        (x >= 31 && x <= 33 && y >= 11 && y <= 13) ||
        (x >= 14 && x <= 16 && y >= 19 && y <= 21) ||
        (x >= 26 && x <= 28 && y >= 19 && y <= 21)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'grid_maze',
    name: 'Grid Maze',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 64, y: 150 }, team: Team.Attackers, index: 0 },
      { position: { x: 64, y: 350 }, team: Team.Attackers, index: 1 },
      { position: { x: 64, y: 550 }, team: Team.Attackers, index: 2 },
      { position: { x: 64, y: 750 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 64, y: 150 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 64, y: 350 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 64, y: 550 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 64, y: 750 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 6: SKY BRIDGE - Three parallel lanes with connecting bridges
// ============================================================================

export function createSkyBridgeMap(): GameMap {
  const tileSize = 32;
  const width = 48;
  const height = 36;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Top lane walls (y = 8)
      else if (y === 8 && x > 3 && x < width - 4) {
        // Gaps for bridges at x=15, x=32
        if ((x >= 14 && x <= 16) || (x >= 31 && x <= 33)) {
          row.push(0);
        } else {
          row.push(1);
        }
      }
      // Bottom lane walls (y = 27)
      else if (y === 27 && x > 3 && x < width - 4) {
        if ((x >= 14 && x <= 16) || (x >= 31 && x <= 33)) {
          row.push(0);
        } else {
          row.push(1);
        }
      }
      // Middle lane cover blocks
      else if (
        (x >= 10 && x <= 12 && y >= 16 && y <= 19) ||
        (x >= 22 && x <= 25 && y >= 15 && y <= 20) ||
        (x >= 35 && x <= 37 && y >= 16 && y <= 19)
      ) {
        row.push(1);
      }
      // Site A enclosure (top right)
      else if (
        (x >= 38 && x <= 44 && y === 3) ||
        (x === 38 && y >= 3 && y <= 7)
      ) {
        row.push(1);
      }
      // Site B enclosure (bottom right)
      else if (
        (x >= 38 && x <= 44 && y === height - 4) ||
        (x === 38 && y >= height - 8 && y <= height - 4)
      ) {
        row.push(1);
      }
      // Left spawn protection
      else if (x === 6 && ((y >= 10 && y <= 14) || (y >= 21 && y <= 25))) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'sky_bridge',
    name: 'Sky Bridge',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 80, y: 180 }, team: Team.Attackers, index: 0 },
      { position: { x: 80, y: 400 }, team: Team.Attackers, index: 1 },
      { position: { x: 80, y: 620 }, team: Team.Attackers, index: 2 },
      { position: { x: 80, y: 900 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 80, y: 180 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 80, y: 400 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 80, y: 620 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 80, y: 900 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 1248, y: 96, width: 192, height: 160 },   // Site A (top-right)
      { x: 1248, y: height * tileSize - 256, width: 192, height: 160 },  // Site B (bottom-right)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 7: DATA VAULT - Secure vault with multiple entry points
// ============================================================================

export function createDataVaultMap(): GameMap {
  const tileSize = 32;
  const width = 44;
  const height = 34;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Outer vault walls (forms a large rectangle on right side)
      else if (x === 24 && y >= 4 && y <= height - 5) {
        // Entry doors at specific points
        if ((y >= 8 && y <= 10) || (y >= 16 && y <= 17) || (y >= 23 && y <= 25)) {
          row.push(0);
        } else {
          row.push(1);
        }
      }
      else if (y === 4 && x >= 24 && x < width - 1) {
        row.push(1);
      }
      else if (y === height - 5 && x >= 24 && x < width - 1) {
        row.push(1);
      }
      // Inner vault walls (two secure rooms)
      else if (x === 32 && y >= 6 && y <= 13) {
        if (y >= 9 && y <= 10) row.push(0); // Door
        else row.push(1);
      }
      else if (x === 32 && y >= height - 14 && y <= height - 7) {
        if (y >= height - 12 && y <= height - 11) row.push(0); // Door
        else row.push(1);
      }
      else if (y === 6 && x >= 32 && x <= 40) {
        row.push(1);
      }
      else if (y === 13 && x >= 32 && x <= 40) {
        row.push(1);
      }
      else if (y === height - 14 && x >= 32 && x <= 40) {
        row.push(1);
      }
      else if (y === height - 7 && x >= 32 && x <= 40) {
        row.push(1);
      }
      // Left side cover
      else if (
        (x >= 6 && x <= 8 && y >= 8 && y <= 11) ||
        (x >= 12 && x <= 14 && y >= 14 && y <= 19) ||
        (x >= 6 && x <= 8 && y >= 22 && y <= 25) ||
        (x >= 16 && x <= 18 && y >= 8 && y <= 10) ||
        (x >= 16 && x <= 18 && y >= 23 && y <= 25)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'data_vault',
    name: 'Data Vault',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 64, y: 200 }, team: Team.Attackers, index: 0 },
      { position: { x: 64, y: 400 }, team: Team.Attackers, index: 1 },
      { position: { x: 64, y: 600 }, team: Team.Attackers, index: 2 },
      { position: { x: 64, y: 800 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 80, y: 300 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 80, y: 450 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 80, y: 600 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 80, y: 750 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 1056, y: 224, width: 224, height: 192 },   // Site A (top vault room)
      { x: 1056, y: height * tileSize - 416, width: 224, height: 192 },  // Site B (bottom vault room)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 8: NEON TOWER - Vertical feel with open platforms
// ============================================================================

export function createNeonTowerMap(): GameMap {
  const tileSize = 32;
  const width = 36;
  const height = 40;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Horizontal platforms (floor-like)
      else if (y === 8 && x >= 4 && x <= 14) {
        row.push(1);
      }
      else if (y === 8 && x >= 21 && x <= 31) {
        row.push(1);
      }
      else if (y === 16 && x >= 8 && x <= 27) {
        // Gap in middle
        if (x >= 16 && x <= 19) row.push(0);
        else row.push(1);
      }
      else if (y === 24 && x >= 4 && x <= 31) {
        // Gaps for movement
        if ((x >= 10 && x <= 12) || (x >= 23 && x <= 25)) row.push(0);
        else row.push(1);
      }
      else if (y === 32 && x >= 8 && x <= 27) {
        if (x >= 16 && x <= 19) row.push(0);
        else row.push(1);
      }
      // Vertical pillars
      else if (
        (x === 8 && y >= 10 && y <= 14) ||
        (x === 27 && y >= 10 && y <= 14) ||
        (x === 12 && y >= 26 && y <= 30) ||
        (x === 23 && y >= 26 && y <= 30)
      ) {
        row.push(1);
      }
      // Center structure
      else if (x >= 15 && x <= 20 && y >= 18 && y <= 22) {
        row.push(1);
      }
      // Corner covers
      else if (
        (x >= 3 && x <= 5 && y >= 3 && y <= 5) ||
        (x >= width - 6 && x <= width - 4 && y >= 3 && y <= 5) ||
        (x >= 3 && x <= 5 && y >= height - 6 && y <= height - 4) ||
        (x >= width - 6 && x <= width - 4 && y >= height - 6 && y <= height - 4)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'neon_tower',
    name: 'Neon Tower',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 100, y: 100 }, team: Team.Attackers, index: 0 },
      { position: { x: 200, y: 100 }, team: Team.Attackers, index: 1 },
      { position: { x: 100, y: 200 }, team: Team.Attackers, index: 2 },
      { position: { x: 200, y: 200 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 100, y: height * tileSize - 100 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 200, y: height * tileSize - 100 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 100, y: height * tileSize - 200 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 200, y: height * tileSize - 200 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 9: CROSSFIRE - Four-way intersection with open center
// ============================================================================

export function createCrossfireMap(): GameMap {
  const tileSize = 32;
  const width = 40;
  const height = 40;
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Four corner buildings
      else if (x >= 3 && x <= 10 && y >= 3 && y <= 10) {
        // Top-left building with entry
        if ((x === 10 && y >= 6 && y <= 8) || (y === 10 && x >= 6 && x <= 8)) row.push(0);
        else row.push(1);
      }
      else if (x >= width - 11 && x <= width - 4 && y >= 3 && y <= 10) {
        // Top-right building
        if ((x === width - 11 && y >= 6 && y <= 8) || (y === 10 && x >= width - 9 && x <= width - 7)) row.push(0);
        else row.push(1);
      }
      else if (x >= 3 && x <= 10 && y >= height - 11 && y <= height - 4) {
        // Bottom-left building
        if ((x === 10 && y >= height - 9 && y <= height - 7) || (y === height - 11 && x >= 6 && x <= 8)) row.push(0);
        else row.push(1);
      }
      else if (x >= width - 11 && x <= width - 4 && y >= height - 11 && y <= height - 4) {
        // Bottom-right building
        if ((x === width - 11 && y >= height - 9 && y <= height - 7) || (y === height - 11 && x >= width - 9 && x <= width - 7)) row.push(0);
        else row.push(1);
      }
      // Center cover (small cross)
      else if (
        (x >= centerX - 2 && x <= centerX + 2 && y >= centerY - 1 && y <= centerY + 1) ||
        (x >= centerX - 1 && x <= centerX + 1 && y >= centerY - 2 && y <= centerY + 2)
      ) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'crossfire',
    name: 'Crossfire',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 64, y: centerY * tileSize - 50 }, team: Team.Attackers, index: 0 },
      { position: { x: 64, y: centerY * tileSize + 50 }, team: Team.Attackers, index: 1 },
      { position: { x: 100, y: centerY * tileSize - 100 }, team: Team.Attackers, index: 2 },
      { position: { x: 100, y: centerY * tileSize + 100 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 64, y: centerY * tileSize - 50 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 64, y: centerY * tileSize + 50 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 100, y: centerY * tileSize - 100 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 100, y: centerY * tileSize + 100 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 10: WAREHOUSE - Industrial storage with crates
// ============================================================================

export function createWarehouseMap(): GameMap {
  const tileSize = 32;
  const width = 44;
  const height = 32;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Crate rows (staggered pattern)
      else if (y >= 5 && y <= 7 && ((x >= 8 && x <= 10) || (x >= 16 && x <= 18) || (x >= 24 && x <= 26) || (x >= 32 && x <= 34))) {
        row.push(1);
      }
      else if (y >= 12 && y <= 14 && ((x >= 5 && x <= 7) || (x >= 13 && x <= 15) || (x >= 21 && x <= 23) || (x >= 29 && x <= 31) || (x >= 37 && x <= 39))) {
        row.push(1);
      }
      else if (y >= 19 && y <= 21 && ((x >= 8 && x <= 10) || (x >= 16 && x <= 18) || (x >= 24 && x <= 26) || (x >= 32 && x <= 34))) {
        row.push(1);
      }
      else if (y >= 26 && y <= 28 && ((x >= 5 && x <= 7) || (x >= 13 && x <= 15) || (x >= 21 && x <= 23) || (x >= 29 && x <= 31) || (x >= 37 && x <= 39))) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'warehouse',
    name: 'Warehouse',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 64, y: 160 }, team: Team.Attackers, index: 0 },
      { position: { x: 64, y: 320 }, team: Team.Attackers, index: 1 },
      { position: { x: 64, y: 480 }, team: Team.Attackers, index: 2 },
      { position: { x: 64, y: 640 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 64, y: 160 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 64, y: 320 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 64, y: 480 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 64, y: 640 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 11: SERVER FARM - High-tech data center with spike sites
// ============================================================================

export function createServerFarmMap(): GameMap {
  const tileSize = 32;
  const width = 48;
  const height = 36;

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Server rack rows (vertical lines)
      else if (x === 10 && y >= 4 && y <= height - 5) {
        if ((y >= 10 && y <= 12) || (y >= 22 && y <= 24)) row.push(0); // gaps
        else row.push(1);
      }
      else if (x === 18 && y >= 4 && y <= height - 5) {
        if ((y >= 15 && y <= 19)) row.push(0);
        else row.push(1);
      }
      else if (x === 28 && y >= 4 && y <= height - 5) {
        if ((y >= 15 && y <= 19)) row.push(0);
        else row.push(1);
      }
      else if (x === 36 && y >= 4 && y <= height - 5) {
        if ((y >= 10 && y <= 12) || (y >= 22 && y <= 24)) row.push(0);
        else row.push(1);
      }
      // Site A enclosure (top right)
      else if ((y === 5 && x >= 38 && x <= 44) || (x === 38 && y >= 5 && y <= 12)) {
        row.push(1);
      }
      // Site B enclosure (bottom right)
      else if ((y === height - 6 && x >= 38 && x <= 44) || (x === 38 && y >= height - 13 && y <= height - 6)) {
        row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'server_farm',
    name: 'Server Farm',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 80, y: 200 }, team: Team.Attackers, index: 0 },
      { position: { x: 80, y: 400 }, team: Team.Attackers, index: 1 },
      { position: { x: 80, y: 600 }, team: Team.Attackers, index: 2 },
      { position: { x: 80, y: 800 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 80, y: 280 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 80, y: 450 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 80, y: 620 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 80, y: 790 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 1248, y: 192, width: 192, height: 192 },   // Site A (top-right)
      { x: 1248, y: height * tileSize - 384, width: 192, height: 192 },  // Site B (bottom-right)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// ============================================================================
// MAP 12: NEXUS HUB - Central control room with spike sites
// ============================================================================

export function createNexusHubMap(): GameMap {
  const tileSize = 32;
  const width = 46;
  const height = 38;
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);

  const tiles: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      // Border walls
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        row.push(1);
      }
      // Central control room (hexagon-ish)
      else if (
        (x >= centerX - 5 && x <= centerX + 5 && (y === centerY - 4 || y === centerY + 4)) ||
        (y >= centerY - 4 && y <= centerY + 4 && (x === centerX - 5 || x === centerX + 5))
      ) {
        // Leave entrances
        if (
          (y === centerY && (x === centerX - 5 || x === centerX + 5)) ||
          (x === centerX && (y === centerY - 4 || y === centerY + 4))
        ) {
          row.push(0);
        } else {
          row.push(1);
        }
      }
      // Left spawn area cover
      else if ((x >= 6 && x <= 8 && y >= 12 && y <= 14) || (x >= 6 && x <= 8 && y >= 23 && y <= 25)) {
        row.push(1);
      }
      // Site A (top right corner room)
      else if (
        (x === 32 && y >= 4 && y <= 12) ||
        (y === 4 && x >= 32 && x <= 42) ||
        (y === 12 && x >= 32 && x <= 42)
      ) {
        // Entry door
        if (x === 32 && y >= 7 && y <= 9) row.push(0);
        else row.push(1);
      }
      // Site B (bottom right corner room)
      else if (
        (x === 32 && y >= height - 13 && y <= height - 5) ||
        (y === height - 5 && x >= 32 && x <= 42) ||
        (y === height - 13 && x >= 32 && x <= 42)
      ) {
        // Entry door
        if (x === 32 && y >= height - 10 && y <= height - 8) row.push(0);
        else row.push(1);
      }
      else {
        row.push(0);
      }
    }
    tiles.push(row);
  }

  const mapData: MapData = {
    id: 'nexus_hub',
    name: 'Nexus Hub',
    width: width * tileSize,
    height: height * tileSize,
    tileSize,
    tiles,
    spawnPoints: [
      { position: { x: 80, y: 300 }, team: Team.Attackers, index: 0 },
      { position: { x: 80, y: 450 }, team: Team.Attackers, index: 1 },
      { position: { x: 80, y: 600 }, team: Team.Attackers, index: 2 },
      { position: { x: 80, y: 750 }, team: Team.Attackers, index: 3 },
      { position: { x: width * tileSize - 80, y: 250 }, team: Team.Defenders, index: 0 },
      { position: { x: width * tileSize - 80, y: 400 }, team: Team.Defenders, index: 1 },
      { position: { x: width * tileSize - 80, y: 700 }, team: Team.Defenders, index: 2 },
      { position: { x: width * tileSize - 80, y: 850 }, team: Team.Defenders, index: 3 },
    ],
    spikeSites: [
      { x: 1056, y: 160, width: 288, height: 224 },   // Site A (top-right room)
      { x: 1056, y: height * tileSize - 384, width: 288, height: 224 },  // Site B (bottom-right room)
    ],
    reactorPath: [],
    props: [],
  };

  return new GameMap(mapData);
}

// Legacy function for backwards compatibility
export function createTestMap(): GameMap {
  return createNeonArenaMap();
}
